import axios from "config/axios/base";
import qs from "qs";

/** Default page size for Skyflow patient list endpoints (explicit so BE does not use a larger default). */
export const PATIENT_LIST_PER_PAGE = 25;

export const getPatients = ({ query = {}, options = null }) =>
	axios.get(`/skyflow/getPatientsList?${qs.stringify(query)}`, options);
export const advancedSearchInStartNotePage = ({ query = {}, options = null }) =>
	axios.get(`/skyflow/advanced_search_in_start_note_page?${qs.stringify(query)}`, options);
export const advancedSearchInAll = ({ query = {}, options = null }) =>
	axios.get(`/skyflow/advanced_search_in_all?${qs.stringify(query)}`, options);
export const patientRegistrationLookup = ({ query = {}, options = null }) =>
	axios.get(`/skyflow/patient_registration_lookup?${qs.stringify(query)}`, options);
export const getSinglePatientOrders = ({ params = {}, query = {}, options = null }) =>
	axios.get(`/patient/${params?.id}/orders?${qs.stringify(query)}`, options);
export const getSinglePatientReferrals = ({ params = {}, query = {}, options = null }) =>
	axios.get(`/patient/${params?.id}/internal-referrals?${qs.stringify(query)}`, options);
export const getPatientDashboardReports = ({ query = {}, options = null }) =>
	axios.get(`/patient/dashboard-reports?${qs.stringify(query)}`, options);
export const getPatientValidation = ({ query = {}, options = null }) =>
	axios.get(`/skyflow/validation?${qs.stringify(query)}`, options);
export const patientIntakeLookup = ({ body = {}, options = null }) => axios.post(`/intake/lookup`, body, options);
export const getPatientConnections = ({ params = {}, query = {}, options = null }) =>
	axios.get(`/patient/connections/${params?.id}?${qs.stringify(query)}`, options);
export const getPatientPharmacies = ({ query = {}, options = null }) =>
	axios.get(`/patient/pharmacies?${qs.stringify(query)}`, options);
export const getPatientLabs = ({ query = {}, options = null }) =>
	axios.get(`/patient/labs?${qs.stringify(query)}`, options);
export const postResendPatientInvitation = ({ body = {}, options = null }) =>
	axios.post("/patient/physician/invite/resend", body, options);
export const deleteSinglePatientInvitation = ({ params = {}, options = null }) =>
	axios.delete(`/patient/physician/invite/${params?.id}/revoke`, options);
export const postPatientSendInvitation = ({ body = {}, options = null }) =>
	axios.post("/patient/physician/invite", body, options);
export const getPatientDocuments = ({ query = {}, options = null }) =>
	axios.get(`/patient/documents?${qs.stringify(query)}`, options);
/** Body `file_type` must be accepted by API, including LAB_RESULTS, DIAGNOSTIC_TEST, DIAGNOSTIC_IMAGING. */
export const postPatientDocuments = ({ body = {}, options = null }) => axios.post("/patient/documents", body, options);
export const getSinglePatientSharedDocuments = ({ params = {}, query = {}, options = null }) =>
	axios.get(`/patient/documents/shared/${params?.id}?${qs.stringify(query)}`, options);
export const getPatientsSharedDocuments = ({ query = {}, options = null }) =>
	axios.get(`/patient/documents/shared?${qs.stringify(query)}`, options);
export const postPatientsSharedDocumentAsSeen = ({ params = {}, options = null }) =>
	axios.post(`/patient/documents/shared/seen/${params?.id}`, options);
export const deleteSinglePatientDocument = ({ params = {}, options = null }) =>
	axios.delete(`/patient/documents/${params?.id}`, options);
export const getLabPatients = ({ query = {}, options = null }) =>
	axios.get(`/skyflow/lab/getPatientsList?${qs.stringify(query)}`, options);
export const getPatientConnectionBySegment = ({ params = {}, options = null }) =>
	axios.get(`/patient/connection/segment/${params?.id}`, options);
export const postPatientDocumentShare = ({ body = {}, options = null }) =>
	axios.post("/patient/documents/share", body, options);
/** Body `file_type` must be accepted by API, including LAB_RESULTS, DIAGNOSTIC_TEST, DIAGNOSTIC_IMAGING. */
export const postPhysicianDocumentShare = ({ params = {}, body = {}, options = null }) =>
	axios.post(`/patient/documents/physician-share/${params?.id}`, body, options);
export const getAppointmentPatients = ({ query = {}, options = null }) =>
	axios.get(`/skyflow/search?${qs.stringify(query)}`, options);
export const postPatientDocumentReshare = ({ params = {}, body = {}, options = null }) =>
	axios.post(`/patient/documents/${params?.document}/reshare`, body, options);

const resolvePatientProviderDisconnectPath = (patientId) => {
	const raw = (process.env.REACT_APP_PATIENT_PROVIDER_DISCONNECT_PATH || "").trim();
	if (!raw) return `/patient-provider/${patientId}/disconnect`;
	return raw.split(":patientId").join(patientId).split("{{patientId}}").join(patientId).split(":id").join(patientId);
};

/**
 * Portal (Passport session) patient–provider disconnect — always **HTTP DELETE**.
 *
 * Path: `/patient-provider/{id}/disconnect` relative to axios `/api` baseURL, unless
 * `REACT_APP_PATIENT_PROVIDER_DISCONNECT_PATH` is set (template with `:patientId`, `{{patientId}}`, or `:id`).
 *
 * Optional JSON body on the DELETE (e.g. `{ source: "portal" }`) is sent as Axios `data`.
 *
 * If you see 404 `Route not found`, Laravel has not registered this DELETE route on that environment yet.
 * Whitelabel integrations use a different contract: `POST /intake-token/patient-provider/disconnect` with `X-API-KEY`, not this call.
 */
export const deletePatientProviderDisconnect = ({ params = {}, body = {}, options = null }) => {
	const id = params?.id;
	const path = resolvePatientProviderDisconnectPath(id);
	return axios.delete(path, { ...(options || {}), data: body });
};

export const getPatientImportOptions = ({ options = null } = {}) => axios.get("/patients/import/options", options);

export const getPatientImportSampleCsv = ({ options = null } = {}) =>
	axios.get("/patients/import/sample.csv", { responseType: "blob", ...(options || {}) });

export const postPatientImportCsv = ({ body = {}, options = null } = {}) => {
	const formData = new FormData();
	if (body?.file) formData.append("file", body.file);

	return axios.post("/patients/import/csv", formData, {
		...(options || {}),
		headers: {
			"Content-Type": "multipart/form-data",
			"x-prevent-flashes": "true",
			...(options?.headers || {}),
		},
	});
};
